"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { CircleHelp } from "lucide-react"
import type { HardConstraintClarification } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { useI18n } from "@/i18n"
import { pendingLineKey } from "@/lib/constraintClarification"

/**
 * ADR-080: a legacy "Milk / lactose" row (migration 0045) waits for ONE
 * non-blocking answer. This line stays on Today until the user answers under
 * Food & health — nothing else is blocked, and the safe reading holds meanwhile.
 * Reads `/v1/me.pendingClarifications` itself so Today's load stays untouched.
 */
export function ClarificationLine() {
  const api = useApiClient()
  const { t } = useI18n()
  const [pending, setPending] = useState<HardConstraintClarification[]>([])
  useEffect(() => {
    let cancelled = false
    // The line is a nudge, never a blocker — any failed read (or a client
    // without this read at all) shows nothing.
    Promise.resolve()
      .then(() => api.getMe())
      .then(me => {
        if (!cancelled) setPending(me.pendingClarifications ?? [])
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [api])
  const key = pendingLineKey(pending)
  if (!key) return null
  return (
    <div
      role="status"
      data-testid="clarification-line"
      className="flex items-center gap-3 rounded-bb-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-ink"
    >
      <CircleHelp aria-hidden="true" className="size-5 shrink-0 text-amber-700" />
      <span className="flex-1">{t(`views.today.${key}`)}</span>
      <Link to="/app/more/constraints" className="font-semibold underline underline-offset-2">
        {t("views.today.clarifyCta")}
      </Link>
    </div>
  )
}
